// On a phone, screens move like an app's (B-313, DIRECTION U20).
//
// Going deeper slides the new screen in from the right, coming back slides it
// in from the left, and switching tabs does not move at all, as a tab bar's
// switch does not (Apple's tab bars; Material's navigation bar). About a
// quarter of a second on Material's standard curve; with reduced motion, a
// short fade, which Chrome's guidance prefers to none ("reduced motion
// doesn't mean the user wants no motion"). Where the browser has already
// animated a swipe back (iOS's edge swipe, `hasUAVisualTransition`), ours is
// skipped so nothing moves twice.
//
// Only the new screen moves, drawn from its first paint: the old one is gone
// by then, as a router swaps it. The address's query (a week, a filter) and a
// sheet's own entry change nothing on screen, so they do not move it.
//
// WHAT A TAB DID, THE TAB BAR SAYS (`tabMoves`): a switch stands still, and
// going back to a tab's first screen moves as Back does. Any other way of
// moving between screens — a team's name on Matchup opening its page under
// League — is going deeper, whichever tabs the two are under. (The first cut
// guessed a switch from the two addresses' tabs, which stood a name's link
// still and slid a tab tapped from a screen under no tab; the review of
// 2026-10-13.)

import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { isMorphing } from './morph';

/** The browser animated the last Back itself. */
let browserMoved = false;
/** What the next screen change is, when the tab bar made it, and when it said so. */
let fromTab: { how: 'still' | 'back'; at: number } | null = null;

/**
 * The tab bar is about to change the screen: a switch, or back to a tab's
 * first screen. Heard by the next change only if it comes at once, so a tap
 * that changed nothing is not taken for the next link's.
 */
export function tabMoves(how: 'still' | 'back'): void {
  fromTab = { how, at: Date.now() };
}
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => {
    browserMoved =
      (event as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true;
  });
}

/** Restart the content area's entrance for each new screen. */
export function useScreenMotion(area: RefObject<HTMLElement | null>, on: boolean): void {
  const { pathname } = useLocation();
  const type = useNavigationType();
  const last = useRef<string | null>(null);
  useLayoutEffect(() => {
    const before = last.current;
    last.current = pathname;
    const el = area.current;
    const tab = fromTab !== null && Date.now() - fromTab.at < 1000 ? fromTab.how : null;
    if (!on || el === null || before === null || before === pathname) return;
    fromTab = null;
    const move =
      // A morph moves the screen itself; nothing moves twice.
      tab === 'still' || type === 'REPLACE' || isMorphing()
        ? null
        : tab === 'back'
          ? 'back'
          : type === 'POP'
            ? browserMoved
              ? null
              : 'back'
            : 'deeper';
    browserMoved = false;
    delete el.dataset.enter;
    if (move === null) return;
    // A read of the layout between, so the same move twice runs twice.
    void el.offsetWidth;
    el.dataset.enter = move;
  }, [pathname]);
}
